import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ChevronRight, ArrowLeft, Search, ShoppingCart, Filter, Grid, List, Package } from 'lucide-react';
import api from '../utils/api';
import { resolveImageUrl } from '../lib/imageUrl';

const PanasonicProductsPage = () => {
  const { worldSlug, categorySlug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { t, lang } = useI18n();
  
  const [world, setWorld] = useState(null);
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [breadcrumb, setBreadcrumb] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const limit = 20;

  useEffect(() => {
    fetchData();
  }, [worldSlug, categorySlug, page, searchTerm, lang]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch world
      const worldRes = await api.get(`/panasonic/worlds/${worldSlug}`);
      setWorld(worldRes.data);

      // Find category by slug
      const allCats = await api.get(`/panasonic/worlds/${worldSlug}/categories`);
      let cat = allCats.data.find(c => c.slug === categorySlug);
      
      // If not found in top-level, it might be a subcategory
      if (!cat) {
        // Fetch all categories recursively (simplified - just checking one level deep)
        for (const topCat of allCats.data) {
          if (topCat.has_subcategories) {
            const subCats = await api.get(`/panasonic/worlds/${worldSlug}/categories?parent_id=${topCat.id}`);
            cat = subCats.data.find(c => c.slug === categorySlug);
            if (cat) break;
          }
        }
      }

      if (cat) {
        // Fetch full category details
        const catRes = await api.get(`/panasonic/categories/${cat.id}`);
        setCategory(catRes.data);
        setBreadcrumb(catRes.data.breadcrumb || []);

        // Fetch products
        const productsRes = await api.get(`/panasonic/categories/${cat.id}/products`, {
          params: { skip: page * limit, limit, search: searchTerm || undefined }
        });
        setProducts(productsRes.data.products);
        setTotal(productsRes.data.total);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Fehler beim Laden der Produkte');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
    toast.success(`${product.name} wurde zum Warenkorb hinzugefügt`);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(price);
  };

  const getBackLink = () => {
    if (breadcrumb.length > 1) {
      const parentBreadcrumb = breadcrumb[breadcrumb.length - 2];
      if (parentBreadcrumb && !parentBreadcrumb.is_world) {
        return `/panasonic/${worldSlug}/${parentBreadcrumb.slug}`;
      }
    }
    return `/panasonic/${worldSlug}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="panasonic-products-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(getBackLink())}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 me-2" />
        {t('backTo', { name: breadcrumb[breadcrumb.length - 2]?.name || world?.name || '' })}
      </Button>

      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6 flex-wrap">
        <Link to="/panasonic" className="hover:text-primary">
          {t('productWorlds')}
        </Link>
        {breadcrumb.map((item, index) => (
          <React.Fragment key={item.id || index}>
            <ChevronRight className="h-4 w-4" />
            {index === breadcrumb.length - 1 ? (
              <span className="text-gray-900 font-medium">{item.name}</span>
            ) : (
              <Link 
                to={item.is_world ? `/panasonic/${item.slug}` : `/panasonic/${worldSlug}/${item.slug}`}
                className="hover:text-primary"
              >
                {item.name}
              </Link>
            )}
          </React.Fragment>
        ))}
      </nav>

      {/* Header */}
      <div className="mb-6">
        <span className="text-sm text-gray-500">Panasonic</span>
        <h1 className="text-2xl md:text-3xl font-bold mb-2">{category?.name}</h1>
        {category?.description && (
          <p className="text-gray-600">{category.description}</p>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              data-testid="panasonic-product-search"
              placeholder={t('searchProduct')}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              className="ps-10"
            />
          </div>
          <span className="text-sm text-gray-500">
            {t('productsFound', { count: total })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="panasonic-grid-view-button"
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button
            data-testid="panasonic-list-view-button"
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Products */}
      {products.length === 0 ? (
        <div className="bg-white border border-border rounded-lg p-12 text-center">
          <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold mb-2">{t('noProducts')}</h3>
          <p className="text-gray-500">
            {searchTerm 
              ? t('tryOtherSearch') 
              : t('noProductsInCategory')
            }
          </p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products.map((product) => (
            <div 
              key={product.id}
              data-testid={`panasonic-grid-product-${product.id}`}
              className="bg-white border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
            >
              {/* Product Image */}
              <Link to={`/product/${product.id}`}>
                <div className="h-48 bg-white relative">
                  {product.image_url ? (
                    <img 
                      src={resolveImageUrl(product.image_url)}
                      alt={product.name}
                      className="w-full h-full object-contain p-4"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="h-12 w-12 text-gray-300" />
                    </div>
                  )}
                  {/* Brand Badge */}
                  <div className="absolute top-2 left-2 bg-white/90 px-2 py-0.5 rounded text-xs font-medium">
                    Panasonic
                  </div>
                </div>
              </Link>

              {/* Product Info */}
              <div className="p-4">
                <Link to={`/product/${product.id}`}>
                  <h3 className="font-semibold mb-1 hover:text-primary transition-colors line-clamp-2">
                    {product.name}
                  </h3>
                </Link>
                {product.sku && (
                  <p className="text-xs text-gray-500 mb-2" data-testid={`panasonic-grid-sku-${product.id}`}>{t('artNr')}: {product.sku}</p>
                )}
                {product.description && (
                  <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                    {product.description}
                  </p>
                )}
                
                {/* Price */}
                <div className="flex items-end justify-between">
                  <div>
                    <span className="text-xl font-bold text-primary">
                      {formatPrice(product.price)}
                    </span>
                    {user && (
                      <span className="text-xs text-gray-500 block" data-testid={`panasonic-grid-net-label-${product.id}`}>
                        {t('priceNet')}
                      </span>
                    )}
                  </div>
                  <Button 
                    size="sm" 
                    onClick={() => handleAddToCart(product)}
                    data-testid={`panasonic-grid-add-to-cart-${product.id}`}
                  >
                    <ShoppingCart className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // List View
        <div className="space-y-4">
          {products.map((product) => (
            <div 
              key={product.id}
              data-testid={`panasonic-list-product-${product.id}`}
              className="bg-white border border-border rounded-lg p-4 flex gap-4 hover:shadow-md transition-shadow"
            >
              {/* Product Image */}
              <Link to={`/product/${product.id}`} className="flex-shrink-0">
                <div className="w-24 h-24 bg-white border rounded overflow-hidden">
                  {product.image_url ? (
                    <img 
                      src={resolveImageUrl(product.image_url)}
                      alt={product.name}
                      className="w-full h-full object-contain p-2"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="h-8 w-8 text-gray-300" />
                    </div>
                  )}
                </div>
              </Link>

              {/* Product Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <span className="text-xs text-gray-500">Panasonic</span>
                    <Link to={`/product/${product.id}`}>
                      <h3 className="font-semibold hover:text-primary transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    {product.sku && (
                      <p className="text-xs text-gray-500" data-testid={`panasonic-list-sku-${product.id}`}>{t('artNr')}: {product.sku}</p>
                    )}
                    {product.description && (
                      <p className="text-sm text-gray-600 line-clamp-1 mt-1">
                        {product.description}
                      </p>
                    )}
                  </div>
                  <div className="text-end flex-shrink-0">
                    <span className="text-xl font-bold text-primary">
                      {formatPrice(product.price)}
                    </span>
                    {user && (
                      <span className="text-xs text-gray-500 block" data-testid={`panasonic-list-net-label-${product.id}`}>{t('priceNet')}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-end mt-2 gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigate(`/product/${product.id}`)} data-testid={`panasonic-list-details-${product.id}`}>
                    {t('details')}
                  </Button>
                  <Button size="sm" onClick={() => handleAddToCart(product)} data-testid={`panasonic-list-add-to-cart-${product.id}`}>
                    <ShoppingCart className="h-4 w-4 me-1" />
                    {t('addToCart')}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            {t('back')}
          </Button>
          <span className="text-sm text-gray-600 mx-4">
            {t('pageOf', { page: page + 1, total: Math.ceil(total / limit) })}
          </span>
          <Button
            variant="outline"
            onClick={() => setPage(p => p + 1)}
            disabled={(page + 1) * limit >= total}
          >
            {t('next')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PanasonicProductsPage;
